import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { MODAL_DISMISS_REASONS } from '../../modal-dismiss-reasons';

@Component({
    selector: 'chassis-modal-window',
    templateUrl: './window.component.html',
    styleUrls: ['./window.component.scss'],
})
export class ModalWindow implements OnInit, AfterViewInit, OnDestroy {

    /**
     * Add class attribute to the host element.
     *
     * @type {string}
     */
    @HostBinding('class') @Input() classes = 'modal fade show';

    /**
     * Add role attribute to the host element.
     *
     * @type {string}
     */
    @HostBinding('attr.role') role = 'dialog';

    /**
     * Add tabindex attribute to the host element.
     *
     * @type {string}
     */
    @HostBinding('attr.tabindex') tabIndex = '-1';

    /**
     * Add z-index attribute to the host element.
     *
     * @type {string}
     */
    @HostBinding('style.z-index') @Input() zIndex;

    /**
     * Element that is focused prior to modal opening.
     */
    private elementWithFocus: Element;

    /**
     * Indicates if a backdrop has to be shown.
     *
     * @type {boolean}
     */
    @Input() backdrop: boolean | 'static' = true;

    /**
     * Indicates if the modal is closeable by pressing esc on keyboard.
     *
     * @type {boolean}
     */
    @Input() keyboard = true;

    /**
     * The modal window size.
     */
    @Input() size: string;

    /**
     * The dismiss event which is used by the modal ref.
     *
     * @type {EventEmitter<any>}
     */
    @Output('dismiss') dismissEvent = new EventEmitter();

    /**
     * The keyboard esc press listener.
     *
     * @param {Event} event
     */
    @HostListener('keyup.esc', ['$event'])
    onKeyUpEsc(event: Event) {
        this.escKey(event);
    };

    /**
     * The backdrop click listener.
     *
     * @param {Event} event
     */
    @HostListener('click', ['$event'])
    onBackdropClick(event: Event) {
        this.backdropClick(event);
    };

    constructor(private elementRef: ElementRef, private renderer: Renderer2) {
    }

    ngOnInit(): void {
        this.addClassesToBodyElement();

        this.addFocusOnElement();
    }

    private addFocusOnElement() {
        this.elementWithFocus = document.activeElement;
    }

    ngAfterViewInit() {
        if (!this.elementRef.nativeElement.contains(document.activeElement)) {
            this.elementRef.nativeElement['focus'].apply(this.elementRef.nativeElement, []);
        }

        setTimeout(() => {
            this.classes = this.classes.concat(' in');
        }, 0);
    }

    /**
     * Add the modal-open class to the body element.
     */
    private addClassesToBodyElement(): void {
        this.renderer.addClass(document.body, 'modal-open');
    }

    /**
     * The backdrop click callback.
     *
     * @param {Event} event
     */
    public backdropClick(event: Event): void {
        if (this.backdrop === true && this.elementRef.nativeElement === event.target) {
            this.dismiss(MODAL_DISMISS_REASONS.BACKDROP_CLICK);
        }
    }

    /**
     * The keyboard esc press callback.
     *
     * @param {Event} event
     */
    public escKey(event: Event): void {
        if (this.keyboard && !event.defaultPrevented) {
            this.dismiss(MODAL_DISMISS_REASONS.ESC);
        }
    }

    /**
     * Emit a dismiss event with the given reason.
     *
     * @param {MODAL_DISMISS_REASONS} reason
     */
    public dismiss(reason: MODAL_DISMISS_REASONS): void {
        this.dismissEvent.emit(reason);
    }

    ngOnDestroy(): void {
        this.removeClassesFromBodyElement();

        this.removeFocusFromElement();
    }

    private removeFocusFromElement() {
        const body        = document.body;
        const elWithFocus = this.elementWithFocus;

        let elementToFocus;
        if (elWithFocus && elWithFocus['focus'] && body.contains(elWithFocus)) {
            elementToFocus = elWithFocus;
        } else {
            elementToFocus = body;
        }
        elementToFocus['focus'].apply(elementToFocus, []);

        this.elementWithFocus = null;
    }

    /**
     * Remove the modal-open class from the body element.
     */
    private removeClassesFromBodyElement(): void {
        this.renderer.removeClass(document.body, 'modal-open');
    }

}




// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/modals/components/window/window.component.ts